/* ============================================================
   SESAAT STUDIO v3.9 — SHELL PENUH
   ============================================================
   Struktur diambil daripada app.cal.com (diukur):
     sidebar 264px · latar telus · garis kanan 1px rgba(255,255,255,.06)
     nav 14px/500 · tinggi 32px · aktif = isian 4% putih, radius 10px
     kandungan #141414 · versi app di bawah sekali

   Fail ini mengeksport DUA benda:

     StudioShell (default) — shell penuh skrin untuk halaman PROTOTAIP
       (/dashboard-v3.9, /workspace-v3.9, …). `position: fixed` supaya
       shell lama tidak kelihatan di belakangnya.

     StudioRail (named)    — rel menu SAHAJA, untuk dipasang pada app
       SEBENAR sebagai ganti NewSidebar. Ia hidup sebagai anak flex biasa
       (bukan fixed) dan menguruskan lebarnya sendiri.

   ⛔ MOD PROTOTAIP TIDAK BOLEH BERUBAH. Semua tambahan untuk app sebenar
      dipagar dengan `modSebenar` supaya halaman -v3.9 kekal betul-betul
      seperti sebelum ini. Kalau anda menambah sesuatu di sini, tanya:
      "patutkah halaman prototaip nampak benda ni?" Jawapannya hampir
      selalu tidak.
   ============================================================ */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    CalendarDays, Layers, Package, Users, Receipt,
    Megaphone, LifeBuoy, GraduationCap, Settings, ArrowLeftFromLine, Puzzle,
    LayoutDashboard, Store, StickyNote, ShieldCheck, ChevronRight,
    PanelLeftClose, PanelLeftOpen, CirclePlay,
    Zap, CreditCard, Sparkles, Tag, PenLine, Brain, Rocket,
    Download, Cloud, Wand2, RefreshCw, AlertTriangle, LogOut, Folder, Quote, Workflow,
} from 'lucide-react';

import { auth } from '../firebase';
import { signOut } from 'firebase/auth';

import './tokens.css';
import { NavRow, ContentPanel } from './ui';
import { klikBiasa } from '../utils/klikBiasa';
import { APP_VERSION } from '../version';
import { useT } from '../i18n';

/* Setiap menu ada DUA laluan:

     path    — halaman prototaip v3.9 (data contoh). Dipakai oleh
               StudioShell pada route -v3.9.
     sebenar — halaman betul dalam app yang sedang hidup. Dipakai oleh
               StudioRail apabila suis reka bentuk v3.9 dihidupkan.

   ⚠️ Menu TANPA `sebenar` bermakna halaman betulnya BELUM WUJUD. Menu itu
      DISOROK dalam mod sebenar — lebih baik ia tiada daripada menghantar
      pengguna ke skrin kosong. Bila halaman betulnya siap nanti, cukup
      tambah `sebenar` di sini dan menu itu muncul sendiri.

   ⚠️ `admin` bermakna route sebenarnya dipagar `isAdmin` dalam App.jsx.
      Tanpa pagar yang sama di sini, pelanggan biasa klik menu itu dan
      mendarat pada skrin kosong (route tidak wujud untuk mereka).
      Pagar ini HANYA dikenakan dalam mod sebenar; halaman prototaip
      memang admin-only sepenuhnya, jadi ia tidak terjejas.

   ⚠️ `labelKey` — kunci kamus bahasa. Pemalar aras-modul TIDAK boleh
      memanggil useT(), jadi labelnya diterjemah pada masa render oleh
      `teksMenu()` di dalam StudioRail. Menu yang HANYA ada `label`
      (tanpa `labelKey`) sengaja tidak diterjemah: itu semuanya alat
      admin, dan alat admin memang kekal dengan terma asalnya. */
const GROUPS = [
    [
        { id: 'dashboard', label: 'Dashboard', labelKey: 'sidebar.dashboard', icon: LayoutDashboard, path: '/dashboard-v3.9', sebenar: '/dashboard' },
        { id: 'formula', label: 'Formulas', icon: Layers, path: '/formula', sebenar: '/formula', admin: true },
        /* `path` (menu halaman lakaran, admin sahaja) kini ke pilot Generate V2 — arahan user 2026-10-09:
           "ganti kat menu yang route /workspace-v3.9 duduk". Lakaran lama masih hidup di alamat
           /workspace-v3.9. `sebenar` (/workspace — menu app sebenar pelanggan) TIDAK diubah. */
        { id: 'workspace', label: 'Workspace', labelKey: 'sidebar.workspace', icon: CalendarDays, path: '/workspace-v4', sebenar: '/workspace' },
        /* WORKFLOW — galeri workflow Flow (26 Okt 2026). ⛔ `admin: true` BERPASANGAN dengan pagar `isAdmin`
           route '/workflow' dalam App.jsx — buka KEDUA-DUA serentak. Tiada `path` prototaip (corak Hooks). */
        { id: 'workflow', label: 'Workflow', icon: Workflow, sebenar: '/workflow', admin: true },
        { id: 'products', label: 'Products', labelKey: 'sidebar.products', icon: Package, path: '/products-v3.9', sebenar: '/products' },
        /* Extension: DISOROK dalam app sebenar (arahan user 2026-08-17) —
           kad Extension di ATAS rel sudah membawa ke halaman yang SAMA,
           jadi baris ini hanya menduakan jalan yang sama dan pelanggan
           terpaksa memilih antara dua benda yang serupa. `sebenar` sengaja
           DIKEKALKAN: halaman /extension memang wujud, cuma jalannya kini
           kad sahaja. Ia masih muncul pada halaman PELAN kerana kad itu
           tidak dipaparkan di sana (kad = mod sebenar sahaja), jadi tanpa
           baris ini pelan reka bentuk Extension langsung tiada jalan. */
        { id: 'extension', label: 'Extension', labelKey: 'sidebar.extension', icon: Puzzle, path: '/extension-v3.9', sebenar: '/extension', pelanSahaja: true },
        /* PRO Studio dipindahkan ke sini daripada `menuTambahan` (2026-08-16)
           kerana ia kini ada pasangan prototaip. Ia baris terakhir kumpulan
           ini dalam kedua-dua mod. */
        { id: 'prostudio', label: 'PRO Studio', labelKey: 'sidebar.proStudio', icon: Zap, path: '/prostudio-v3.9', sebenar: '/prostudio' },
        /* BOOST — program MCN / TAP / Bind (PLAN_BOOST.md §🔄 2026-11-04).
           `admin: true` + `suis: 'boost'` = admin sentiasa nampak; pelanggan nampak
           HANYA bila suis `config/boost.open` hidup (prop `bukaBoost`, useBoostBuka.js).
           BERPASANGAN dengan pagar route `/boost` dalam App.jsx (isAdmin || boostBuka) —
           kedua-dua membaca hook yang SAMA, jadi menu dan halaman sentiasa dibuka serentak.
           Tiada `path` prototaip: halaman ini tidak mempunyai pasangan pelan reka bentuk. */
        { id: 'boost', label: 'Boost', icon: Rocket, sebenar: '/boost', admin: true, suis: 'boost' },
        /* HOOK LIBRARY — lead magnet (PROTOTAIP, rupa sahaja).
           ⛔ `admin: true` sengaja, dan ia BERPASANGAN dengan pagar `isAdmin`
           pada route '/hooks' dalam App.jsx. Buka KEDUA-DUA serentak bila
           halaman ini sedia untuk pelanggan — kalau menu dibuka dahulu,
           pelanggan klik dan mendarat pada skrin kosong.
           Tiada `path` prototaip (corak sama Boost): halaman ini hanya ada SATU
           alamat, '/hooks', dan ia route SEBENAR — bukan pasangan '-v3.9'. */
        { id: 'hooks', label: 'Hook Library', icon: Quote, sebenar: '/hooks', admin: true },
    ],
    [
        { id: 'flow-google', label: 'Flow Google', icon: CirclePlay, path: '/flow-google', sebenar: '/flow-google', sebenarOnly: true }, // Diletak tepat atas Marketplace (arahan user 2026-09-27); nama + alamat ditukar dari "Flow" /my-flow (arahan user 2026-09-29)
        { id: 'marketplace', label: 'Marketplace', icon: Store, path: '/marketplace-v3.9', sebenar: '/marketplace' }, // DIBUKA kepada SEMUA pengguna (arahan user 2026-08-21) — dulu admin: true
        {
            id: 'affiliate', label: 'Affiliates', labelKey: 'sidebar.affiliates', icon: Users, path: '/affiliate-v3.9', sebenar: '/affiliate',
            children: [
                { id: 'aff-dash', label: 'Dashboard', labelKey: 'sidebar.dashboard', path: '/affiliate-v3.9', sebenar: '/affiliate' },
                { id: 'aff-ref', label: 'Referrals', labelKey: 'sidebar.referrals', path: '/affiliate/referrals', sebenar: '/affiliate/referrals' },
                { id: 'aff-pay', label: 'Payouts', labelKey: 'sidebar.payouts', path: '/affiliate/payouts', sebenar: '/affiliate/payouts' },
                { id: 'aff-res', label: 'Resources', labelKey: 'sidebar.resources', path: '/affiliate/resources', sebenar: '/affiliate/resources' },
                { id: 'aff-set', label: 'Settings', labelKey: 'sidebar.settings', path: '/affiliate/settings', sebenar: '/affiliate/settings' },
            ],
        },
        // Sales: belum ada halaman sebenar — disorok dalam mod sebenar.
        { id: 'sales', label: 'Sales', icon: Receipt, path: '/sales-v3.9' },
    ],
    [
        { id: 'notes', label: 'Notes', labelKey: 'sidebar.notes', icon: StickyNote, path: '/notes-v3.9', sebenar: '/notes' },
        {
            id: 'admin', label: 'Admin', icon: ShieldCheck, admin: true,
            children: [
                /* Alamat menuding TERUS ke tab pertama, bukan ke `/admin`
                   yang hanya melencong. Satu lompatan kurang, dan alamat
                   dalam bar sepadan dengan apa yang dilihat sejak saat
                   pertama — penting kerana tab kini boleh di-bookmark. */
                { id: 'adm-sys', label: 'System', path: '/admin/system/dashboard', sebenar: '/admin/system/dashboard' },
                /* Formulas — PROTOTAIP DESIGN (27 Okt 2026): urus semua formula + block master prompt.
                   Route '/admin/formulas' dipagar isAdmin dalam App.jsx. */
                /* Label "Formula Settings" (arahan user 27 Okt) — bezakan daripada menu "Formulas" (rak /formula) di atas. */
                { id: 'adm-formulas', label: 'Formula Settings', path: '/admin/formulas', sebenar: '/admin/formulas' },
                { id: 'adm-aff', label: 'Affiliate', path: '/admin/affiliate/overview', sebenar: '/admin/affiliate/overview' },
                { id: 'adm-mkt', label: 'Marketplace', path: '/admin/marketplace/products', sebenar: '/admin/marketplace/products' },
                { id: 'adm-auto', label: 'Automation', path: '/admin/automation/ai16saat', sebenar: '/admin/automation/ai16saat' },
                { id: 'adm-ver', label: 'Versi Skrip', path: '/admin/script-versions', sebenar: '/admin/script-versions' },
                { id: 'adm-announce', label: 'Pengumuman', path: '/admin/announce', sebenar: '/admin/announce' },
                { id: 'adm-boost', label: 'Boost', path: '/admin/boost', sebenar: '/admin/boost' },
                { id: 'adm-partner', label: 'Partners', path: '/admin/partners', sebenar: '/admin/partners' },
                { id: 'adm-lab', label: 'Test Lab', path: '/pipeline-test', sebenar: '/pipeline-test' },
                { id: 'adm-android', label: 'Android Test', path: '/android-test', sebenar: '/android-test' },
            ],
        },
    ],
];

const FOOT = [
    /* Tutorial: DIBUKA kepada semua pelanggan 2026-08-16 (arahan user) —
       pagar `admin: true` di sini dan `isAdmin &&` pada kedua-dua route
       /tutorial dalam App.jsx dibuang serentak. Halaman Extension kini
       menghantar pelanggan ke sini untuk langkah pasang, jadi menu ini
       WAJIB kekal terbuka; kalau ia dipagar semula, pautan itu mati. */
    { id: 'tutorial', label: 'Tutorial', icon: GraduationCap, path: '/tutorial', sebenar: '/tutorial' },
    // Help: belum ada halaman sebenar — disorok dalam mod sebenar.
    { id: 'help', label: 'Help', icon: LifeBuoy, path: '/help-v3.9' },
    { id: 'settings', label: 'Settings', labelKey: 'sidebar.settings', icon: Settings, path: '/settings-v3.9', sebenar: '/profile' },
];

/* ── Menu yang HANYA wujud dalam app sebenar ──────────────────────────
   Semua ini ada dalam NewSidebar. Ia dipindahkan ke sini supaya bertukar
   kepada rel v3.9 tidak menghilangkan satu pun jalan yang pengguna sudah
   biasa guna. Ia TIDAK muncul pada halaman prototaip.

   ⚠️ Menu "Pricing" berubah label ikut siapa yang melihat — itu memang
      kelakuan asal dalam NewSidebar, bukan sesuatu yang direka di sini:
        trial  → "Langgan Sekarang" (CTA — ini pintu upgrade, jangan buang)
        paid   → "Pricing"
        admin  → "Harga"                                                */
function menuTambahan({ isAdmin, isPrivileged, userTier }) {
    const hargaItem = userTier === 'trial'
        ? { id: 'pricing', label: 'Langgan Sekarang', labelKey: 'sidebar.subscribeNow', icon: Sparkles, sebenar: '/pricing', ctaUpgrade: true }
        : isAdmin
            ? { id: 'pricing', label: 'Harga', icon: CreditCard, sebenar: '/pricing' }
            : (userTier === 'paid' && !isPrivileged)
                ? { id: 'pricing', label: 'Pricing', labelKey: 'sidebar.pricing', icon: CreditCard, sebenar: '/pricing' }
                : null;

    return [
        /* ⚠️ Kumpulan KOSONG yang SENGAJA dikekalkan — jangan buang.
           Pemanggil menyatukan senarai ini dengan GROUPS mengikut INDEKS
           (`tambah.forEach((extra, i) => asas[i].push(...extra))`), jadi
           membuang array ini menolak kumpulan Pricing naik ke GROUPS[0]
           dan menu tersasar kumpulan.
           Isinya dulu: Products (Baru)/(Table) — prototaip yang dibuang
           2026-08-16. PRO Studio pula sudah pindah ke GROUPS; jangan
           tambah semula di sini, ia akan muncul dua kali. */
        [],
        [
            hargaItem,
            { id: 'pricing-baru', label: 'Pricing (Baru)', icon: Tag, sebenar: '/pricing-baru', admin: true },
        ].filter(Boolean),
        [
            { id: 'rewrite', label: 'Rewrite', icon: PenLine, sebenar: '/rewrite', admin: true },
            { id: 'ai-logic', label: 'AI Logic', icon: Brain, sebenar: '/ai-instructions', admin: true },
        ],
    ];
}

/* Buang menu yang tiada halaman sebenar, dan menu admin bagi bukan-admin.
   Menu induk yang SEMUA anaknya tersingkir turut dibuang — kalau tidak ia
   jadi butang kembang yang membuka senarai kosong. */
/* `suisBuka` = { [item.suis]: bool } — menu `admin: true` yang dibuka kepada pelanggan
   melalui suis config (cth Boost). Tiada suis / suis mati = kekal admin sahaja. */
function tapisMenu(kumpulan, modSebenar, bolehAdmin, suisBuka = {}) {
    if (!modSebenar) return kumpulan.filter(item => !item.sebenarOnly);
    return kumpulan
        .map((item) => {
            /* `pelanSahaja` = menu yang memang ada halaman sebenar, tetapi
               sengaja tidak dipaparkan dalam app sebenar kerana ada jalan
               lain yang lebih baik ke halaman itu (contoh: kad Extension). */
            if (item.pelanSahaja) return null;
            if (item.admin && !bolehAdmin && !(item.suis && suisBuka[item.suis])) return null;
            if (!item.children) return item.sebenar ? item : null;
            const anak = item.children.filter((c) => c.sebenar);
            return anak.length ? { ...item, children: anak } : null;
        })
        .filter(Boolean);
}

const laluan = (item, modSebenar) => (modSebenar ? item.sebenar : item.path) || item.path;

/* Padanan alamat semasa → menu mana yang patut bertanda aktif, untuk mod
   sebenar. Disusun PANJANG DAHULU: '/admin/affiliate' mesti diuji sebelum
   '/admin', kalau tidak semua halaman admin bertanda "Kelola Sistem".
   Padanan hanya menerima alamat yang SAMA atau yang diikuti '/', supaya
   '/products-v3.9' tidak tersilap dikira sebagai '/products'. */
const PADANAN_AKTIF = [
    ['/admin/affiliate', 'adm-aff'],
    ['/admin/marketplace', 'adm-mkt'],
    ['/admin/automation', 'adm-auto'],
    ['/pipeline-test', 'adm-lab'],
    ['/android-test', 'adm-android'],
    ['/admin/script-versions', 'adm-ver'],
    ['/admin/announce', 'adm-announce'],
    ['/admin/boost', 'adm-boost'],
    ['/admin/partners', 'adm-partner'],
    ['/admin/formulas', 'adm-formulas'],
    ['/admin/system', 'adm-sys'],
    ['/admin', 'adm-sys'],   // bookmark lama `/admin`, sebelum ia melencong
    ['/affiliate/referrals', 'aff-ref'],
    ['/affiliate/payouts', 'aff-pay'],
    ['/affiliate/resources', 'aff-res'],
    ['/affiliate/settings', 'aff-set'],
    ['/affiliate', 'aff-dash'],
    ['/pricing-baru', 'pricing-baru'],
    ['/pricing', 'pricing'],
    ['/prostudio', 'prostudio'],
    ['/rewrite', 'rewrite'],
    ['/ai-instructions', 'ai-logic'],
    ['/workspace', 'workspace'],
    ['/products', 'products'],
    ['/extension', 'extension'],
    ['/marketplace', 'marketplace'],
    ['/formula', 'formula'],
    ['/notes', 'notes'],
    ['/tutorial', 'tutorial'],
    ['/profile', 'settings'],
    ['/hooks', 'hooks'],
    ['/workflow', 'workflow'],
    ['/boost', 'boost'],
    ['/flow-google', 'flow-google'],
    ['/dashboard', 'dashboard'],
];

/** Alamat semasa → id menu yang patut bertanda aktif. '' bila tiada padanan. */
export function idAktifDariLaluan(pathname = '') {
    const p = String(pathname).toLowerCase();
    for (const [awalan, id] of PADANAN_AKTIF) {
        if (p === awalan || p.startsWith(`${awalan}/`)) return id;
    }
    return '';
}

/* Keadaan rel menu (terbuka / kuncup) disimpan dalam browser supaya ia
   kekal apabila pengguna tukar halaman atau muat semula. Disimpan
   per-browser, bukan dalam Firestore — ia keutamaan paparan, bukan data. */
const RAIL_KEY = 'sesaat_studio_rail';
const RAIL_LEBAR = { buka: 264, kuncup: 60 };

/* Warna status storan — maksudnya disalin daripada NewSidebar (hijau/
   kuning/merah = makna amaran). Sejak tema terang wujud (2026-08-15)
   ia MESTI token --s-ok/--s-warn/--s-stop: nilai tetap #f59e0b yang
   betul di atas #111 menjadi hampir tidak terbaca di atas putih, dan
   token status memang membawa makna amaran yang sama dalam kedua-dua
   tema — cuma dicerahkan/digelapkan mengikut latar. */
const warnaStorage = (nisbah) => (nisbah > 0.9 ? 'var(--s-stop)' : nisbah > 0.7 ? 'var(--s-warn)' : 'var(--s-ok)');

/* ============================================================
   REL MENU
   ============================================================
   Dipakai dua tempat: di dalam StudioShell (halaman prototaip) dan
   terus di App.jsx sebagai ganti NewSidebar (app sebenar).

   `modSebenar` menentukan ke mana menu menghala, menu mana yang
   dipaparkan, dan sama ada bahagian tambahan (kad Extension, storan,
   baris profil) muncul.
   ============================================================ */
export function StudioRail({
    active = '',
    onNavigate,
    userName = 'User',
    modSebenar = false,
    /* ── Di bawah ini HANYA bermakna dalam mod sebenar ──────────────
       Nilai lalai dipilih supaya rel tetap berfungsi kalau salah satu
       tidak dihantar: tiada admin, tiada storan, tiada space. */
    user = null,
    isAdmin = false,
    isPrivileged = false,
    /* Suis `config/boost.open` (useBoostBuka) — buka menu Boost kepada pelanggan. */
    bukaBoost = false,
    userTier = null,
    userPlan = null,
    spaces = [],
    onSelectSpace,
    storageUsed = 0,
    storageLimitBytes = 2 * 1024 * 1024 * 1024,
    isCleaningStorage = false,
    onCleanupStorage,
    /* Rel ini dipasang DI DALAM laci menu telefon (App.jsx) apabila skrin
       sempit. Dalam keadaan itu ia tidak boleh kuncup: laci sudah pun
       kecil, dan jalur ikon 60px di dalam kotak 280px terbaca sebagai
       menu rosak. App.jsx yang tahu lebar skrin, jadi ia yang beritahu. */
    mobil = false,
}) {
    const location = useLocation();
    const t = useT();

    /* Label baris menu. `labelKey` ada → ambil daripada kamus bahasa;
       kalau tiada, pakai `label` seadanya (menu alat admin — ia sengaja
       kekal dengan terma asalnya dalam kedua-dua bahasa). */
    const teksMenu = (item) => (item.labelKey ? t(item.labelKey) : item.label);

    /* Rel menu boleh dikuncupkan jadi ikon sahaja. Bacaan localStorage
       dibalut try/catch — mod privasi tertentu melemparkan ralat pada
       akses storage, dan itu tidak sepatutnya mematikan seluruh shell. */
    const [kuncupTersimpan, setKuncup] = useState(() => {
        try { return localStorage.getItem(RAIL_KEY) === 'kuncup'; } catch { return false; }
    });

    /* ⚠️ Keadaan kuncup disimpan PER-BROWSER, jadi pelanggan yang pernah
       menguncupkan menu pada komputer membawa keadaan itu ke telefon juga.
       Di dalam laci telefon ia mesti diabaikan — bukan dipadam: bila dia
       kembali ke komputer, menu patut kekal seperti dia tinggalkan. */
    const kuncup = mobil ? false : kuncupTersimpan;

    const tetapkanRail = (nak) => {
        setKuncup(nak);
        try { localStorage.setItem(RAIL_KEY, nak ? 'kuncup' : 'buka'); } catch { /* abaikan */ }
    };

    const bolehAdmin = isAdmin || isPrivileged;

    /* Menu asas + tambahan app sebenar, disatukan ikut kumpulan supaya
       garis pemisah kekal di tempat yang sama seperti reka bentuk asal. */
    const kumpulan = (() => {
        const asas = GROUPS.map((g) => [...g]);
        if (modSebenar) {
            const tambah = menuTambahan({ isAdmin, isPrivileged, userTier });
            tambah.forEach((extra, i) => { if (asas[i]) asas[i].push(...extra); });
        }
        return asas.map((g) => tapisMenu(g, modSebenar, bolehAdmin, { boost: bukaBoost })).filter((g) => g.length);
    })();
    const kaki = tapisMenu(FOOT, modSebenar, bolehAdmin, { boost: bukaBoost });

    /* Menu boleh kembang. Kumpulan yang mengandungi halaman semasa dibuka
       sendiri supaya pengguna nampak di mana dia berada tanpa perlu klik. */
    const [terbuka, setTerbuka] = useState(() => {
        const awal = {};
        for (const g of kumpulan) {
            for (const it of g) {
                if (it.children && it.children.some((c) => c.id === active)) awal[it.id] = true;
            }
        }
        return awal;
    });

    const pergi = (p) => onNavigate?.(p);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            pergi('/');
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    /* ── Paparan pengguna — salinan logik NewSidebar ─────────────────── */
    const namaPapar = user?.displayName || user?.email?.split('@')[0] || userName;
    const initial = (namaPapar || '?').trim().charAt(0).toUpperCase();
    const userPhoto = user?.photoURL;
    // users.plan 'yearly' = Fighter (sama pola PricingSection)
    const isFighter = userTier === 'paid' && userPlan === 'yearly';
    const planLabel = isPrivileged ? (isAdmin ? 'Admin' : 'Moderator')
        : isFighter ? 'Fighter'
            : userTier === 'paid' ? 'Premium'
                : userTier === 'trial' ? 'Free Trial'
                    : 'Free';

    /* ── Storan ──────────────────────────────────────────────────────── */
    const storageRatio = storageLimitBytes ? storageUsed / storageLimitBytes : 0;
    const storageColor = warnaStorage(storageRatio);
    const storageText = storageUsed < 1024 * 1024 * 1024
        ? `${(storageUsed / 1024 / 1024).toFixed(0)} MB`
        : `${(storageUsed / 1024 / 1024 / 1024).toFixed(1)} GB`;
    const storageLimitText = `${(storageLimitBytes / 1024 / 1024 / 1024).toFixed(0)} GB`;
    // Had penuh ("/ 2 GB") duduk dalam tooltip; angka + butang hanya bertukar
    // warna bila ruang mula penuh, selain itu kekal senyap.
    const storageAlert = storageRatio > 0.7;

    const spaceBukanDefault = spaces.filter((s) => s.id !== 'default');
    const tunjukSpace = modSebenar && isPrivileged && spaceBukanDefault.length > 0;

    /* overflowY auto — senarai group menu boleh jadi lebih panjang daripada
       tinggi skrin; dengan `overflow: hidden` item bawah terpotong tanpa
       sebarang cara untuk scroll. */
    return (
        <aside className="sesaat-studio-rail" style={{
            width: kuncup ? RAIL_LEBAR.kuncup : RAIL_LEBAR.buka,
            flex: 'none',
            height: '100%',
            transition: 'width .18s ease',
            /* TIADA garis kanan (arahan user 2026-10-18, ikut cal.com): rel
               duduk rata atas latar halaman, panel kandungan sudah bergaris
               sendiri — garis kedua di sebelahnya hanya bising. */
            padding: kuncup ? '16px 10px 14px' : '16px 12px 14px',
            display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0,
            overflowX: 'hidden', overflowY: 'auto',
        }}>

            {/* Kepala: nama jenama + butang tutup/buka menu sahaja.
                Semasa kuncup hanya butang buka yang tinggal — nama jenama
                tidak muat pada 60px.
                ⛔ Ikon carian + bubble avatar DIBUANG (arahan user 2026-08-15):
                   kedua-duanya hiasan dari prototaip cal.com — bukan butang,
                   klik tak jadi apa-apa. Profil sebenar (nama/pakej/log
                   keluar) memang sudah ada di KAKI rel ini. Jangan pasang
                   balik tanpa membina fungsinya dahulu. */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: kuncup ? 0 : '0 4px',
                justifyContent: kuncup ? 'center' : undefined,
            }}>
                {!kuncup && (
                    <>
                        <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-.01em' }}>Sesaat</span>
                        <span style={{ flex: 1 }} />
                    </>
                )}
                {/* Butang kuncup DISOROK dalam laci telefon: menguncupkan
                    rel di situ tidak menjimatkan apa-apa (laci itu sendiri
                    yang ditutup), dan ia hanya boleh membawa pelanggan ke
                    keadaan jalur-ikon yang mengelirukan. */}
                {!mobil && (
                <button
                    type="button"
                    className="s-railtoggle"
                    aria-expanded={!kuncup}
                    aria-label={t(kuncup ? 'sidebar.openMenu' : 'sidebar.closeMenu')}
                    title={t(kuncup ? 'sidebar.openMenu' : 'sidebar.closeMenu')}
                    onClick={() => tetapkanRail(!kuncup)}
                >
                    {kuncup
                        ? <PanelLeftOpen size={17} strokeWidth={1.9} />
                        : <PanelLeftClose size={17} strokeWidth={1.9} />}
                </button>
                )}
            </div>

            {/* ── Kad Extension — sengaja di ATAS SEKALI ───────────────────
                ⛔ JANGAN turunkan ke bawah atau tukar jadi baris menu biasa.
                   Ini satu-satunya jalan pelanggan memasang extension, dan
                   produk ini TIDAK boleh digunakan tanpa extension. Gaya
                   berbeza daripada baris nav memang disengajakan supaya ia
                   mudah dijumpai. */}
            {modSebenar && (
                <a
                    href="/extension"
                    onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); pergi('/extension'); } }}
                    title={kuncup ? t('sidebar.extCardTip', { versi: APP_VERSION }) : undefined}
                    /* Warna kad (latar terbalik, hover, cincin fokus) ada dalam
                       `.s-extcard` di ui.css — gaya inline di bawah kekal susun
                       atur SAHAJA, kerana ia berubah ikut rel kuncup. */
                    className={`s-extcard${active === 'extension' ? ' s-extcard--on' : ''}`}
                    /* Semasa kuncup, kotak ini mesti SEBIJI saiz dengan kotak
                       baris menu lain (.s-navrow = 32px tinggi, lebar penuh,
                       radius 10px) — kalau tidak rel nampak tak sekata: satu
                       kotak tinggi di atas satu deretan kotak pendek. Tinggi
                       32px termasuk bingkai kerana box-sizing: border-box. */
                    style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        justifyContent: kuncup ? 'center' : 'flex-start',
                        padding: kuncup ? 0 : '10px 12px',
                        /* `flex: none` WAJIB bersama `height` — rel ialah flex
                           column, jadi tanpa ia kad dimampatkan 2px dan kotaknya
                           keluar 30px berbanding 32px baris menu. */
                        height: kuncup ? 32 : undefined, flex: 'none',
                        borderRadius: 10,
                        cursor: 'pointer', width: '100%', textAlign: 'left',
                        boxSizing: 'border-box', textDecoration: 'none',
                    }}
                >
                    {/* Semasa rel kuncup, petak tint di sebalik ikon dibuang:
                        kad sendiri sudah mengecut jadi kotak 48px, jadi tint
                        di dalamnya kelihatan seperti kotak bersarang. Ikon
                        duduk terus atas kotak. */}
                    <span style={{
                        width: 28, height: 28, borderRadius: 8, flex: 'none',
                        display: 'grid', placeItems: 'center',
                        background: kuncup ? 'none' : 'var(--s-primary-fill)',
                        color: 'var(--s-primary-ink)',
                    }}>
                        <Puzzle size={16} strokeWidth={1.9} />
                    </span>
                    {!kuncup && (
                        <>
                            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--s-primary-ink)', lineHeight: 1.3 }}>{t('sidebar.extension')}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--s-primary-ink-2)', lineHeight: 1.3 }}>{t('sidebar.extInstall', { versi: APP_VERSION })}</span>
                            </span>
                            <Download size={15} strokeWidth={1.9} style={{ color: 'var(--s-primary-ink-2)', flex: 'none' }} />
                        </>
                    )}
                </a>
            )}

            {kumpulan.map((group, gi) => (
                <div key={gi} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {group.map((item) => {
                        const Icon = item.icon;

                        if (item.children) {
                            /* Senarai anak tidak pernah dipaparkan semasa rel kuncup —
                               ia teks tanpa ikon, jadi ia jadi jalur kosong pada 60px.
                               Klik pada menu induk semasa kuncup membuka semula rel DAN
                               membuka senarainya, supaya satu klik terus sampai. */
                            const buka = !kuncup && !!terbuka[item.id];
                            const anakAktif = item.children.some((c) => c.id === active);
                            return (
                                <div key={item.id}>
                                    <NavRow
                                        mini={kuncup}
                                        title={kuncup ? teksMenu(item) : undefined}
                                        active={active === item.id || anakAktif}
                                        icon={<Icon size={16} strokeWidth={1.9} />}
                                        aria-expanded={buka}
                                        /* Menu induk yang ADA halamannya sendiri (Affiliates →
                                           /affiliate) dapat alamat itu, supaya ia boleh dibuka
                                           dalam tab baharu. Klik kiri biasa kekal kembang/kuncup.
                                           Induk tanpa halaman sendiri (Admin) kekal butang. */
                                        href={laluan(item, modSebenar) || undefined}
                                        onClick={() => {
                                            if (kuncup) {
                                                tetapkanRail(false);
                                                setTerbuka((p) => ({ ...p, [item.id]: true }));
                                                return;
                                            }
                                            setTerbuka((p) => ({ ...p, [item.id]: !p[item.id] }));
                                        }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ flex: 1 }}>{teksMenu(item)}</span>
                                            <ChevronRight
                                                size={14}
                                                strokeWidth={2}
                                                style={{
                                                    transform: buka ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform .15s ease',
                                                    color: 'var(--s-ink-3)',
                                                }}
                                            />
                                        </span>
                                    </NavRow>

                                    {/* Anak menu — sengaja tiada ikon supaya baris induk kekal
                                        menonjol dan senarai tidak jadi padat. Garis menegak
                                        menunjukkan pertalian tanpa perlu inden yang dalam. */}
                                    {buka && (
                                        <div style={{
                                            marginLeft: 19, paddingLeft: 10,
                                            borderLeft: '1px solid var(--s-line)',
                                            display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2,
                                        }}>
                                            {item.children.map((c) => (
                                                <NavRow
                                                    key={c.id}
                                                    active={active === c.id}
                                                    href={laluan(c, modSebenar)}
                                                    onClick={() => pergi(laluan(c, modSebenar))}
                                                >
                                                    {teksMenu(c)}
                                                </NavRow>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <NavRow
                                key={item.id}
                                mini={kuncup}
                                title={kuncup ? teksMenu(item) : undefined}
                                active={active === item.id}
                                icon={<Icon size={16} strokeWidth={1.9} />}
                                href={laluan(item, modSebenar)}
                                onClick={() => pergi(laluan(item, modSebenar))}
                                /* CTA upgrade — satu-satunya baris menu yang dibenarkan
                                   berwarna. Ia pintu langganan; kalau ia menyepi macam
                                   menu lain, pengguna trial tidak akan perasan. */
                                style={item.ctaUpgrade ? { color: 'var(--s-warn)' } : undefined}
                            >
                                {teksMenu(item)}
                            </NavRow>
                        );
                    })}
                </div>
            ))}

            {/* ── SPACE (admin, hanya bila ada space bukan-default) ───────── */}
            {tunjukSpace && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {!kuncup && (
                        <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: '.08em',
                            color: 'var(--s-ink-3)', padding: '0 8px', marginBottom: 4,
                        }}>SPACE</span>
                    )}
                    {spaceBukanDefault.map((space) => (
                        <NavRow
                            key={space.id}
                            mini={kuncup}
                            title={kuncup ? space.name : undefined}
                            active={location.pathname === `/space/${encodeURIComponent(space.name)}`}
                            icon={<Folder size={16} strokeWidth={1.9} />}
                            href={`/space/${encodeURIComponent(space.name)}`}
                            onClick={() => {
                                pergi(`/space/${encodeURIComponent(space.name)}`);
                                onSelectSpace?.(space.id);
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ flex: 1 }}>{space.name}</span>
                                <span style={{ fontSize: 10, color: 'var(--s-ink-3)', flex: 'none' }}>
                                    {space.productCount || 0}
                                </span>
                            </span>
                        </NavRow>
                    ))}
                </div>
            )}

            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {kaki.map((item) => {
                    const Icon = item.icon;
                    return (
                        <NavRow
                            key={item.id}
                            mini={kuncup}
                            title={kuncup ? teksMenu(item) : undefined}
                            active={active === item.id}
                            icon={<Icon size={16} strokeWidth={1.9} />}
                            href={laluan(item, modSebenar)}
                            onClick={() => pergi(laluan(item, modSebenar))}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {teksMenu(item)}
                                </span>
                            </span>
                        </NavRow>
                    );
                })}

                {/* "Old layout" hanya bermakna dalam mod prototaip — ia jalan
                    keluar daripada halaman contoh kembali ke app sebenar.
                    Dalam mod sebenar app ini MEMANG app sebenar, jadi baris itu
                    tiada makna; jalan patah balik ke rupa lama ialah mematikan
                    suis config/ui_v39. */}
                {!modSebenar && (
                    <NavRow
                        mini={kuncup}
                        title={kuncup ? 'Old layout' : undefined}
                        icon={<ArrowLeftFromLine size={16} strokeWidth={1.9} />}
                        href="/dashboard"
                        onClick={() => pergi('/dashboard')}
                    >
                        Old layout
                    </NavRow>
                )}

                {/* ── Storan: awan · bar · saiz · butang Optimize ──────────────
                    Butang kekal di tempat sama; bila ruang mula penuh (>70%) ia
                    bertukar warna supaya menarik perhatian, bukan berpindah. */}
                {modSebenar && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--s-line)' }}>
                        {kuncup ? (
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => onCleanupStorage?.()}
                                    disabled={isCleaningStorage}
                                    title={t('sidebar.storageTipOptimize', { guna: storageText, had: storageLimitText })}
                                    aria-label={t('sidebar.optimizeStorage')}
                                    style={{
                                        background: 'transparent', border: 0, padding: 6, borderRadius: 8,
                                        color: storageColor,
                                        cursor: isCleaningStorage ? 'not-allowed' : 'pointer',
                                        opacity: isCleaningStorage ? 0.5 : 1,
                                    }}
                                >
                                    {isCleaningStorage
                                        ? <RefreshCw size={17} strokeWidth={2} />
                                        : <Cloud size={17} strokeWidth={2} />}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px' }}
                                    title={t('sidebar.storageTip', { guna: storageText, had: storageLimitText })}
                                >
                                    <Cloud size={14} strokeWidth={2} style={{ color: storageColor, flex: 'none' }} />

                                    <span style={{
                                        flex: 1, minWidth: 28, height: 4, borderRadius: 99,
                                        background: 'var(--s-fill)', overflow: 'hidden',
                                    }}>
                                        <span style={{
                                            display: 'block', height: '100%',
                                            width: `${Math.min(storageRatio * 100, 100)}%`,
                                            background: storageColor,
                                        }} />
                                    </span>

                                    <span style={{
                                        fontSize: 10, fontWeight: 700, flex: 'none', whiteSpace: 'nowrap',
                                        color: storageAlert ? storageColor : 'var(--s-ink-3)',
                                    }}>{storageText}</span>

                                    <button
                                        type="button"
                                        onClick={() => onCleanupStorage?.()}
                                        disabled={isCleaningStorage}
                                        title={t(isCleaningStorage ? 'sidebar.cleaningTip' : 'sidebar.optimizeTip')}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 5, flex: 'none',
                                            padding: '4px 9px', borderRadius: 8,
                                            background: 'var(--s-fill)',
                                            border: `1px solid ${storageAlert ? storageColor : 'transparent'}`,
                                            color: storageAlert ? storageColor : 'var(--s-ink-2)',
                                            fontSize: 10, fontWeight: 700,
                                            opacity: isCleaningStorage ? 0.5 : 1,
                                            cursor: isCleaningStorage ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {isCleaningStorage
                                            ? <RefreshCw size={12} strokeWidth={2.2} />
                                            : <Wand2 size={12} strokeWidth={2.2} />}
                                        {t(isCleaningStorage ? 'sidebar.cleaning' : 'sidebar.optimize')}
                                    </button>
                                </div>

                                {/* Amaran — muncul HANYA bila kritikal (>90%) */}
                                {storageRatio > 0.9 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '0 8px' }}>
                                        <AlertTriangle size={11} strokeWidth={2.4} style={{ color: 'var(--s-stop)', flex: 'none' }} />
                                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--s-stop)' }}>
                                            {t(storageRatio >= 1 ? 'sidebar.storageFull' : 'sidebar.almostFull')}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ── Baris profil: avatar · nama/pakej · Log keluar ──
                    Log keluar WAJIB kekal di sini. Ia ada juga dalam halaman
                    Settings, tetapi pengguna mencarinya di sudut ini — itulah
                    tempatnya sejak dulu.
                    ⛔ Ikon gear Settings DIBUANG (arahan user 2026-08-18) —
                    Settings sudah ada sebagai baris menu utama di atas rel
                    ini, jadi gear di sini pendua. Jangan pasang balik. */}
                {modSebenar && (
                    <div style={{
                        marginTop: 11,
                        display: 'flex', alignItems: 'center',
                        flexDirection: kuncup ? 'column' : 'row',
                        gap: 8, padding: kuncup ? '0' : '0 4px',
                    }}>
                        {userPhoto ? (
                            <img
                                src={userPhoto}
                                alt=""
                                referrerPolicy="no-referrer"
                                style={{ width: 26, height: 26, borderRadius: 99, flex: 'none', objectFit: 'cover' }}
                            />
                        ) : (
                            <span style={{
                                width: 26, height: 26, borderRadius: 99, flex: 'none',
                                display: 'grid', placeItems: 'center',
                                background: 'var(--s-fill-strong)', color: 'var(--s-ink)',
                                fontSize: 11, fontWeight: 600,
                            }}>{initial}</span>
                        )}

                        {!kuncup && (
                            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: 3 }}>
                                <span style={{
                                    fontSize: 12, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.2,
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>{namaPapar}</span>
                                <span style={{
                                    alignSelf: 'flex-start',
                                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '.05em', lineHeight: 1,
                                    color: 'var(--s-ink-2)', background: 'var(--s-fill-hover)',
                                    padding: '2px 6px', borderRadius: 99,
                                }}>{planLabel}</span>
                            </span>
                        )}

                        <button
                            type="button"
                            onClick={handleLogout}
                            title={t('common.logout')}
                            aria-label={t('common.logout')}
                            style={{
                                background: 'transparent', border: 0, padding: 5, borderRadius: 7,
                                color: 'var(--s-ink-3)', cursor: 'pointer', flex: 'none',
                            }}
                        >
                            <LogOut size={16} strokeWidth={1.9} />
                        </button>
                    </div>
                )}
            </div>
        </aside>
    );
}

/* ============================================================
   SHELL PENUH (halaman prototaip -v3.9)
   ============================================================
   Menutup seluruh skrin (position: fixed) supaya shell lama tidak
   kelihatan di belakangnya.
   ============================================================ */
export default function StudioShell({ active = 'formula', onNavigate, userName = 'User', children }) {
    /* `s-root` WAJIB kekal dalam className di bawah — semua token --s-*
       ditakrif pada kelas itu (design/tokens.css). Ia mesti DIGABUNG dengan
       kelas lain dalam SATU atribut className; kalau ditulis sebagai atribut
       className kedua yang berasingan, JSX buang yang pertama dan seluruh
       tema gelap senyap tidak terpakai. */
    return (
        <div
            className="s-root sesaat-studio-shell"
            style={{
                position: 'fixed', inset: 0, zIndex: 50,
                display: 'grid',
                /* `auto` — lebar diambil daripada rel itu sendiri, yang
                   menguruskan keadaan kuncupnya sendiri. */
                gridTemplateColumns: 'auto 1fr',
                background: 'var(--s-ground)',
            }}
        >
            <StudioRail active={active} onNavigate={onNavigate} userName={userName} />

            {/* ---------------- Kandungan ----------------
                Bekas panel duduk dalam design/ui.jsx supaya halaman v3.9
                yang dipasang pada app SEBENAR memakai bentuk yang sama.
                Latar tidak perlu dicat di sini — grid di atas sudah. */}
            <ContentPanel>{children}</ContentPanel>
        </div>
    );
}
